/**
 * Cross-tab refresh 리더 선출 헬퍼.
 *
 * 우선순위:
 * 1. `navigator.locks.request` (Chromium 69+, Firefox 96+, Safari 15.4+)
 * 2. localStorage sentinel fallback — 완벽한 상호배제는 아니지만, 5초 TTL 기반 낙관적 경합.
 *
 * 주의: 락 리소스 이름에는 사용자 식별 정보를 절대 포함하지 말 것.
 */

const lockResourceName = 'agentteams-auth-refresh';
const fallbackLeaderKey = 'agentteams-auth-refresh-leader';
const fallbackTtlMs = 5000;

const hasNavigatorLocks = () =>
  typeof navigator !== 'undefined' &&
  typeof (navigator as Navigator & { locks?: LockManager }).locks?.request === 'function';

const now = () => Date.now();

const generateTabId = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `${now()}-${Math.random().toString(36).slice(2)}`;
};

type LeaderRecord = {
  tabId: string;
  acquiredAt: number;
};

const readLeader = (): LeaderRecord | null => {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(fallbackLeaderKey);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as LeaderRecord;
    if (typeof parsed.tabId !== 'string' || typeof parsed.acquiredAt !== 'number') {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
};

const tryAcquireFallback = (tabId: string): boolean => {
  if (typeof window === 'undefined') return true;
  const existing = readLeader();
  const fresh = existing && now() - existing.acquiredAt < fallbackTtlMs;
  if (fresh) return false;
  try {
    window.localStorage.setItem(fallbackLeaderKey, JSON.stringify({ tabId, acquiredAt: now() } satisfies LeaderRecord));
    // 같은 타이밍에 다른 탭이 썼을 가능성을 확인
    const verified = readLeader();
    return verified?.tabId === tabId;
  } catch {
    return false;
  }
};

const releaseFallback = (tabId: string) => {
  if (typeof window === 'undefined') return;
  const current = readLeader();
  if (!current || current.tabId !== tabId) return;
  try {
    window.localStorage.removeItem(fallbackLeaderKey);
  } catch {
    // swallow
  }
};

/**
 * 주어진 작업을 cross-tab 배타 락 내부에서 실행한다.
 * 리더가 아니면 기존 리더 작업이 끝날 때까지 대기 후 실행 기회를 얻는다.
 */
export const runWithRefreshLeadership = async <T>(task: () => Promise<T>): Promise<T> => {
  if (hasNavigatorLocks()) {
    return (navigator as Navigator & { locks: LockManager }).locks.request(
      lockResourceName,
      { mode: 'exclusive' },
      async () => task(),
    );
  }

  // Fallback: localStorage sentinel + 짧은 재시도 루프
  const tabId = generateTabId();
  const deadline = now() + fallbackTtlMs;

  while (!tryAcquireFallback(tabId)) {
    if (now() > deadline) break;
    await new Promise((resolve) => setTimeout(resolve, 50));
  }

  try {
    return await task();
  } finally {
    releaseFallback(tabId);
  }
};
